import React from 'react';
import styled, { css } from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';
import { spacing } from '../tokens/spacing';
import { borders } from '../tokens/borders';
import { breakpoints } from '../tokens/breakpoints';
import { getPatternStyles, PatternType, PatternIntensity } from '../../utils/backgroundPatterns';

/**
 * Container - Responsive max-width container
 */
export type ContainerSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';
export type PaddingVariant = 'none' | 'sm' | 'md' | 'lg' | 'xl';

interface ContainerProps {
  size?: ContainerSize;
  padding?: PaddingVariant;
  pattern?: PatternType;
  patternIntensity?: PatternIntensity;
  children: React.ReactNode;
  className?: string;
}

const ContainerStyled = styled.div<{
  size: ContainerSize;
  padding: PaddingVariant;
  pattern?: PatternType;
}>`
  width: 100%;
  margin: 0 auto;
  
  ${props => {
    const maxWidths = {
      sm: '640px',
      md: '768px',
      lg: '1024px',
      xl: '1280px',
      full: '100%',
    };
    return `max-width: ${maxWidths[props.size]};`;
  }}

  ${props => {
    const paddings = {
      none: spacing[0],
      sm: spacing[4],
      md: spacing[6],
      lg: spacing[8],
      xl: spacing[12],
    };
    return `padding-left: ${paddings[props.padding]}; padding-right: ${paddings[props.padding]};`;
  }}

  ${props => {
    if (props.pattern && props.pattern !== 'none') {
      const patternStyles = getPatternStyles(
        props.pattern,
        {
          background: props.theme.colors.background,
          text: props.theme.colors.text,
        },
        'subtle'
      );
      return css`
        background-image: ${patternStyles.backgroundImage};
        background-size: ${patternStyles.backgroundSize};
        background-position: ${patternStyles.backgroundPosition};
      `;
    }
    return '';
  }}

  @media (max-width: ${breakpoints.md}) {
    padding-left: ${spacing[4]};
    padding-right: ${spacing[4]};
  }
`;

export const Container: React.FC<ContainerProps> = ({
  size = 'xl',
  padding = 'md',
  pattern,
  patternIntensity = 'subtle',
  children,
  className,
}) => {
  const theme = useTheme();
  return (
    <ContainerStyled
      size={size}
      padding={padding}
      pattern={pattern}
      className={className}
    >
      {children}
    </ContainerStyled>
  );
};

/**
 * Section - Semantic section wrapper
 */
export type SectionVariant = 'default' | 'subtle' | 'accent';

interface SectionProps {
  variant?: SectionVariant;
  spacing?: PaddingVariant;
  pattern?: PatternType;
  children: React.ReactNode;
  className?: string;
}

const SectionStyled = styled.section<{
  variant: SectionVariant;
  spacing: PaddingVariant;
  pattern?: PatternType;
}>`
  ${props => {
    const paddings = {
      none: spacing[0],
      sm: spacing[4],
      md: spacing[6],
      lg: spacing[8],
      xl: spacing[12],
    };
    return `padding: ${paddings[props.spacing]} 0;`;
  }}

  ${props => {
    switch (props.variant) {
      case 'subtle':
        return `background: ${props.theme.colors.surfaceHover};`;
      case 'accent':
        return `background: ${props.theme.colors.overlayLight};`;
      default:
        return `background: ${props.theme.colors.background};`;
    }
  }}

  ${props => {
    if (props.pattern && props.pattern !== 'none') {
      const patternStyles = getPatternStyles(
        props.pattern,
        {
          background: props.theme.colors.background,
          text: props.theme.colors.text,
        },
        'subtle'
      );
      return css`
        background-image: ${patternStyles.backgroundImage};
        background-size: ${patternStyles.backgroundSize};
        background-position: ${patternStyles.backgroundPosition};
      `;
    }
    return '';
  }}
`;

export const Section: React.FC<SectionProps> = ({
  variant = 'default',
  spacing = 'md',
  pattern,
  children,
  className,
}) => {
  return (
    <SectionStyled
      variant={variant}
      spacing={spacing}
      pattern={pattern}
      className={className}
    >
      {children}
    </SectionStyled>
  );
};

/**
 * Grid - Responsive grid system
 */
export type GridColumns = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
export type GridGap = 'none' | 'sm' | 'md' | 'lg' | 'xl';

interface GridProps {
  columns?: GridColumns | { sm?: GridColumns; md?: GridColumns; lg?: GridColumns };
  gap?: GridGap;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

const GridStyled = styled.div<{
  columns: GridColumns | { sm?: GridColumns; md?: GridColumns; lg?: GridColumns };
  gap: GridGap;
}>`
  display: grid;
  
  ${props => {
    const gaps = {
      none: spacing[0],
      sm: spacing[2],
      md: spacing[4],
      lg: spacing[6],
      xl: spacing[8],
    };
    return `gap: ${gaps[props.gap]};`;
  }}

  ${props => {
    if (typeof props.columns === 'number') {
      return `grid-template-columns: repeat(${props.columns}, 1fr);`;
    }
    
    const cols = props.columns;
    let styles = '';
    
    if (cols.sm) {
      styles += `
        @media (min-width: ${breakpoints.sm}) {
          grid-template-columns: repeat(${cols.sm}, 1fr);
        }
      `;
    }
    if (cols.md) {
      styles += `
        @media (min-width: ${breakpoints.md}) {
          grid-template-columns: repeat(${cols.md}, 1fr);
        }
      `;
    }
    if (cols.lg) {
      styles += `
        @media (min-width: ${breakpoints.lg}) {
          grid-template-columns: repeat(${cols.lg}, 1fr);
        }
      `;
    }
    
    return styles;
  }}

  /* Auto-fit fallback */
  @media (max-width: ${breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

export const Grid: React.FC<GridProps> = ({
  columns = 12,
  gap = 'md',
  children,
  className,
  style,
}) => {
  return (
    <GridStyled columns={columns} gap={gap} className={className} style={style}>
      {children}
    </GridStyled>
  );
};

/**
 * Stack - Vertical/horizontal stacking
 */
export type StackDirection = 'row' | 'column';
export type StackAlign = 'start' | 'center' | 'end' | 'stretch';
export type StackJustify = 'start' | 'center' | 'end' | 'space-between' | 'space-around' | 'space-evenly';

interface StackProps {
  direction?: StackDirection;
  spacing?: PaddingVariant;
  align?: StackAlign;
  justify?: StackJustify;
  wrap?: boolean;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

const StackStyled = styled.div<{
  direction: StackDirection;
  spacing: PaddingVariant;
  align: StackAlign;
  justify: StackJustify;
  wrap: boolean;
}>`
  display: flex;
  flex-direction: ${props => props.direction};
  flex-wrap: ${props => (props.wrap ? 'wrap' : 'nowrap')};
  align-items: ${props => props.align};
  justify-content: ${props => props.justify};

  ${props => {
    const gaps = {
      none: spacing[0],
      sm: spacing[2],
      md: spacing[4],
      lg: spacing[6],
      xl: spacing[8],
    };
    const gap = gaps[props.spacing];
    
    if (props.direction === 'row') {
      return `gap: 0 ${gap};`;
    }
    return `gap: ${gap} 0;`;
  }}

  > * {
    ${props => {
      if (props.spacing === 'none') return '';
      const gaps = {
        none: spacing[0],
        sm: spacing[2],
        md: spacing[4],
        lg: spacing[6],
        xl: spacing[8],
      };
      const gap = gaps[props.spacing];
      
      if (props.direction === 'row') {
        return `
          &:not(:last-child) {
            margin-right: ${gap};
          }
        `;
      }
      return `
        &:not(:last-child) {
          margin-bottom: ${gap};
        }
      `;
    }}
  }
`;

export const Stack: React.FC<StackProps> = ({
  direction = 'column',
  spacing = 'md',
  align = 'stretch',
  justify = 'start',
  wrap = false,
  children,
  className,
  style,
}) => {
  return (
    <StackStyled
      direction={direction}
      spacing={spacing}
      align={align}
      justify={justify}
      wrap={wrap}
      className={className}
      style={style}
    >
      {children}
    </StackStyled>
  );
};

/**
 * Flex - Flexbox wrapper
 */
export type FlexDirection = 'row' | 'column' | 'row-reverse' | 'column-reverse';
export type FlexWrap = 'nowrap' | 'wrap' | 'wrap-reverse';

interface FlexProps {
  direction?: FlexDirection;
  wrap?: FlexWrap;
  align?: StackAlign;
  justify?: StackJustify;
  gap?: PaddingVariant;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

const FlexStyled = styled.div<{
  direction: FlexDirection;
  wrap: FlexWrap;
  align: StackAlign;
  justify: StackJustify;
  gap: PaddingVariant;
}>`
  display: flex;
  flex-direction: ${props => props.direction};
  flex-wrap: ${props => props.wrap};
  align-items: ${props => props.align};
  justify-content: ${props => props.justify};

  ${props => {
    const gaps = {
      none: spacing[0],
      sm: spacing[2],
      md: spacing[4],
      lg: spacing[6],
      xl: spacing[8],
    };
    return `gap: ${gaps[props.gap]};`;
  }}
`;

export const Flex: React.FC<FlexProps> = ({
  direction = 'row',
  wrap = 'nowrap',
  align = 'stretch',
  justify = 'start',
  gap = 'md',
  children,
  className,
  style,
}) => {
  return (
    <FlexStyled
      direction={direction}
      wrap={wrap}
      align={align}
      justify={justify}
      gap={gap}
      className={className}
      style={style}
    >
      {children}
    </FlexStyled>
  );
};
